'use client'

import { useState } from 'react'
import { useForm } from 'react-hook-form'
import { zodResolver } from '@hookform/resolvers/zod'
import { useTranslations } from 'next-intl'
import { Link } from '@/i18n/navigation'
import { Button } from '@/components/ui/Button'
import type { FormContent, PfeOffer } from '@/types/content'
import { CV_CONSTRAINTS, pfeApplicationSchema, type PfeApplicationInput } from '@/lib/forms/schemas'
import {
  CheckboxField,
  FileField,
  HoneypotField,
  SelectField,
  TextareaField,
  TextField,
} from './Field'
import { FormFeedback } from './FormFeedback'
import { useFormSubmit } from './useFormSubmit'

interface PfeApplicationFormProps {
  content: FormContent
  offers: PfeOffer[]
  /** Offre pré-sélectionnée quand on arrive depuis une page de sujet. */
  selectedOfferSlug?: string
  /** Paramètre du site : les candidatures spontanées peuvent être désactivées. */
  spontaneousEnabled: boolean
}

const MAX_SIZE_MB = CV_CONSTRAINTS.maxSizeBytes / (1024 * 1024)

export function PfeApplicationForm({
  content,
  offers,
  selectedOfferSlug,
  spontaneousEnabled,
}: PfeApplicationFormProps) {
  const t = useTranslations()
  const { state, submit } = useFormSubmit('/api/forms/pfe')
  const [cvError, setCvError] = useState<string>()
  const [cvFile, setCvFile] = useState<File>()

  const {
    register,
    handleSubmit,
    reset,
    formState: { errors },
  } = useForm<PfeApplicationInput>({
    resolver: zodResolver(pfeApplicationSchema),
    defaultValues: { offer: selectedOfferSlug ?? '', consent: false },
  })

  /** Contrôle côté client. Le contrôle qui fait foi reste côté serveur. */
  function validateCv(file: File | undefined): boolean {
    if (!file) {
      setCvError(t('form.required'))
      return false
    }
    if (file.size > CV_CONSTRAINTS.maxSizeBytes) {
      setCvError(t('form.fileTooLarge', { max: MAX_SIZE_MB }))
      return false
    }
    const extension = file.name.slice(file.name.lastIndexOf('.')).toLowerCase()
    if (!CV_CONSTRAINTS.extensions.includes(extension)) {
      setCvError(t('form.fileTypeInvalid', { formats: CV_CONSTRAINTS.extensions.join(', ') }))
      return false
    }
    setCvError(undefined)
    return true
  }

  const onSubmit = handleSubmit(async (values) => {
    if (!validateCv(cvFile)) return

    // multipart : le CV transite en pièce jointe vers le BFF.
    const payload = new FormData()
    for (const [key, value] of Object.entries(values)) {
      if (value !== undefined && value !== null) payload.set(key, String(value))
    }
    if (cvFile) payload.set('cv', cvFile)

    const ok = await submit(payload)
    if (ok) {
      reset({ offer: selectedOfferSlug ?? '', consent: false })
      setCvFile(undefined)
    }
  })

  if (state === 'success') return <FormFeedback state={state} />

  return (
    <form onSubmit={onSubmit} noValidate className="flex flex-col gap-5">
      <HoneypotField {...register('website')} />

      <SelectField
        id="pfe-offer"
        label={content.labels.offer ?? ''}
        required={!spontaneousEnabled}
        error={errors.offer ? t(`form.${errors.offer.message}`) : undefined}
        {...register('offer')}
      >
        {spontaneousEnabled ? <option value="">{content.spontaneousLabel}</option> : null}
        {offers.map((offer) => (
          <option key={offer.id} value={offer.slug}>
            {offer.reference} — {offer.title}
          </option>
        ))}
      </SelectField>

      <div className="grid gap-5 sm:grid-cols-2">
        <TextField
          id="pfe-lastName"
          label={content.labels.lastName ?? ''}
          required
          autoComplete="family-name"
          error={errors.lastName ? t(`form.${errors.lastName.message}`) : undefined}
          {...register('lastName')}
        />
        <TextField
          id="pfe-firstName"
          label={content.labels.firstName ?? ''}
          required
          autoComplete="given-name"
          error={errors.firstName ? t(`form.${errors.firstName.message}`) : undefined}
          {...register('firstName')}
        />
        <TextField
          id="pfe-email"
          type="email"
          label={content.labels.email ?? ''}
          required
          autoComplete="email"
          error={errors.email ? t(`form.${errors.email.message}`) : undefined}
          {...register('email')}
        />
        <TextField
          id="pfe-phone"
          type="tel"
          label={content.labels.phone ?? ''}
          required
          autoComplete="tel"
          error={errors.phone ? t(`form.${errors.phone.message}`) : undefined}
          {...register('phone')}
        />
        <TextField
          id="pfe-school"
          label={content.labels.school ?? ''}
          required
          error={errors.school ? t(`form.${errors.school.message}`) : undefined}
          {...register('school')}
        />
        <TextField
          id="pfe-speciality"
          label={content.labels.speciality ?? ''}
          required
          placeholder={content.placeholders?.speciality}
          error={errors.speciality ? t(`form.${errors.speciality.message}`) : undefined}
          {...register('speciality')}
        />
      </div>

      <TextField
        id="pfe-desiredSubject"
        label={content.labels.desiredSubject ?? ''}
        placeholder={content.placeholders?.desiredSubject}
        error={errors.desiredSubject ? t(`form.${errors.desiredSubject.message}`) : undefined}
        {...register('desiredSubject')}
      />

      <TextareaField
        id="pfe-message"
        label={content.labels.message ?? ''}
        required
        placeholder={content.placeholders?.message}
        error={errors.message ? t(`form.${errors.message.message}`) : undefined}
        {...register('message')}
      />

      <FileField
        id="pfe-cv"
        label={content.labels.cv ?? ''}
        required
        hint={content.fileHint}
        accept={CV_CONSTRAINTS.extensions.join(',')}
        error={cvError}
        onChange={(event) => {
          const file = event.target.files?.[0]
          setCvFile(file)
          if (file) validateCv(file)
        }}
      />

      <CheckboxField
        id="pfe-consent"
        error={errors.consent ? t(`form.${errors.consent.message}`) : undefined}
        {...register('consent')}
      >
        {content.consentText}{' '}
        <Link href="/confidentialite" className="text-primary-700 underline underline-offset-2">
          {content.privacyLinkLabel}
        </Link>
        .
      </CheckboxField>

      {state === 'error' ? <FormFeedback state={state} /> : null}

      <div>
        <Button
          type="submit"
          size="lg"
          disabled={state === 'submitting'}
          label={state === 'submitting' ? t('form.submitting') : content.submitLabel}
          icon="ArrowRight"
        />
      </div>
    </form>
  )
}
